import { map } from 'lodash';
import { Action } from 'redux';
import { from, of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter as filterObs,
  map as mapObservable,
  mergeMap,
  switchMap,
} from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';
import { Annotation } from '../../annotations/models/annotation.model';
import { AnnotationRelation } from '../../annotations/models/annotation-relation.model';
import { AnnotationBounds } from '../../annotations/models/annotation-bounds.model';
import { selectAnnotationsArray } from '../../annotations/selectors/annotation.selectors';
import { selectAnnotationPagesArray } from '../../annotations/selectors/annotation-pages.selectors';
import { AppState } from '../../app-state';
import { AppEpic } from '../../common/types/app-epic.type';
import { handleEpicError } from '../../common/utils/epics';
import {
  appendPredictions,
  fetchPredictionsError,
  nazgulPredictionsSuccess,
  requestNazgulPredictions,
  runPredictions,
} from '../actions/predictions.actions';
import {
  NazgulPredictionResponse,
  Prediction,
} from '../models/predictions.model';
import {
  NazgulPredictionRequest,
  NazgulPredictionResource,
} from '../resources/nazgul-prediction.resource';
import { appendAnnotations } from '../../annotations/actions/annotations.actions';
import { appendAnnotationRelations } from '../../annotations/actions/annotation-relations.actions';
import { KeyValueMap } from '../../common/types/KeyValueMap.type';
import uuid from 'uuid/v4';
import { RELATION_TYPES } from '../../lib/relations/relation-types';

export const runPredictionsEpic: AppEpic = (action$, state$) => {
  return action$.pipe(
    filterObs(isActionOf(runPredictions)),
    mergeMap(action => {
      try {
        const state = state$.value;
        return of(
          requestNazgulPredictions(
            buildNazgulRequest(state, action.payload.annotationTypes),
          ),
        );
      } catch (e) {
        console.error('Error running predictions', e);
        return of(fetchPredictionsError(String(e)));
      }
    }),
  );
};

export const appendPredictionsEpic: AppEpic = (action$, state$) => {
  return action$.pipe(
    filterObs(isActionOf(appendPredictions)),
    concatMap(action => {
      try {
        const state = state$.value;
        const predictions = action.payload;
        const activeUserName = state.users?.activeUserAccount?.user?.username;
        const sourceDocumentId = state.annotations.sourceDocumentId;

        const currentAnnotationsArr = selectAnnotationsArray(state);

        // Build lookup of existing annotations by type+startOffset
        const existingByTypeOffset: { [key: string]: string } = {};
        currentAnnotationsArr.forEach(a => {
          if (a.type && a.start !== undefined) {
            existingByTypeOffset[`${a.type}:${a.start}`] = a.id;
          }
        });

        // Find which predictions duplicate existing annotations,
        // and remap children to point to the existing annotation
        const idRemap: { [predictionId: string]: string } = {};
        predictions.forEach(p => {
          const key = `${p.label}:${p.startOffset}`;
          const existingId = existingByTypeOffset[key];
          if (existingId && p.annotationId) {
            idRemap[p.annotationId] = existingId;
          }
        });

        const dedupedPredictions = predictions
          .filter(p => !p.annotationId || !idRemap[p.annotationId])
          .map(p => {
            if (p.parentAnnotationId && idRemap[p.parentAnnotationId]) {
              return {
                ...p,
                parentAnnotationId: idRemap[p.parentAnnotationId],
              };
            }
            return p;
          });

        if (!activeUserName || !sourceDocumentId) {
          return from([]);
        }
        const annotationPages = selectAnnotationPagesArray(state);
        const annotationsFromPredictions = map(
          dedupedPredictions,
          (p: Prediction, k: number) => {
            const id = p.annotationId || `${p.uuid}-${k}`;
            const bounds =
              p.startOffset !== undefined &&
              p.endOffset !== undefined &&
              annotationPages.length > 0
                ? AnnotationBounds.fromOffsets(
                    p.startOffset,
                    p.endOffset,
                    annotationPages,
                  )
                : undefined;

            const annotation: AnnotationWithParent = {
              id,
              type: p.label,
              start: p.startOffset,
              end: p.endOffset,
              bounds,
              annotatedBy: activeUserName,
              annotatedDate: p.createdAt,
              value: p.value,
              nativeText: p.nativeText,
              predictionId: p.predictionId,
              fromUserSpecificPrediction: true,
              parentAnnotationId: p.parentAnnotationId,
            };
            return annotation;
          },
        );

        const allAnnotations: Annotation[] = currentAnnotationsArr.concat(
          annotationsFromPredictions,
        );

        const relationsFromPredictions = KeyValueMap.usingKey(
          annotationsFromPredictions
            .map(a => relationFromPrediction(a, allAnnotations))
            .filter(r => !!r)
            .map(r => r as AnnotationRelation),
          'id',
        );

        const actions: Action[] = [
          appendAnnotations(
            KeyValueMap.usingKey(annotationsFromPredictions, 'id'),
            sourceDocumentId,
          ),
          appendAnnotationRelations(relationsFromPredictions),
        ];
        return from(actions);
      } catch (e) {
        console.error('Error appending predictions', e);
        return from([fetchPredictionsError(String(e))]);
      }
    }),
  );
};

type AnnotationWithParent = Annotation & {
  parentAnnotationId: string | null;
  bounds?: AnnotationBounds[];
};
function relationFromPrediction(
  annotation: AnnotationWithParent,
  annotations: Annotation[],
) {
  const parentId = annotation.parentAnnotationId || '';
  const parentAnnotation = annotations.find(a => a.id === parentId);

  if (!parentAnnotation) {
    return null;
  }

  const rule = {
    left: annotation.type,
    right: parentAnnotation.type,
    type: RELATION_TYPES.CHILD_OF,
  };

  return {
    id: uuid(),
    rule,
    left: annotation,
    right: parentAnnotation,
  };
}

function buildNazgulRequest(
  state: AppState,
  annotationTypes?: string[],
): NazgulPredictionRequest {
  const sourceDocumentId = state.annotations.sourceDocumentId;
  const documentSetName = state.sourceDocument.meta?.documentSetName || '';

  return {
    document_set: documentSetName,
    target_document_id: sourceDocumentId || 0,
    annotation_types: annotationTypes,
  };
}

export const requestNazgulPredictionsEpic: AppEpic = (action$, state$) => {
  return action$.pipe(
    filterObs(isActionOf(requestNazgulPredictions)),
    switchMap(action => {
      return NazgulPredictionResource.predict(
        action.payload,
        state$.value,
      ).pipe(
        mapObservable(response => nazgulPredictionsSuccess(response)),
        catchError(
          handleEpicError(
            'Error requesting Nazgul predictions',
            fetchPredictionsError,
          ),
        ),
      );
    }),
    catchError(
      handleEpicError(
        'Error in Nazgul predictions epic',
        fetchPredictionsError,
      ),
    ),
  );
};

export const processNazgulPredictionsEpic: AppEpic = (action$, state$) => {
  return action$.pipe(
    filterObs(isActionOf(nazgulPredictionsSuccess)),
    concatMap(action => {
      try {
        const response = action.payload;
        const predictions = convertNazgulResponseToPredictions(
          response,
          state$.value,
        );
        return from([appendPredictions(predictions)]);
      } catch (e) {
        console.error('Error processing Nazgul predictions', e);
        return from([fetchPredictionsError(String(e))]);
      }
    }),
  );
};

function convertNazgulResponseToPredictions(
  response: NazgulPredictionResponse,
  state: AppState,
): Prediction[] {
  const activeUserName = state.users?.activeUserAccount?.user?.username;
  const sourceDocumentId = state.annotations.sourceDocumentId;
  const currentTime = new Date().toISOString();

  if (!activeUserName || !sourceDocumentId) {
    return [];
  }

  const relationMap: { [key: number]: number | null } = {};
  response.predicted_relations.forEach(rel => {
    relationMap[rel.left_attribute_annotation_id] =
      rel.right_attribute_annotation_id;
  });

  return response.predicted_attributes.map((attr, index) => {
    const parentId = relationMap[attr.id];
    const parentAnnotationId = parentId ? `nazgul-attr-${parentId}` : null;
    const timeWithOffset = new Date(
      new Date(currentTime).getTime() + index * 1000,
    ).toISOString();

    return {
      predictionId: undefined,
      uuid: attr.id,
      userSpecific: true,
      documentTextId: 0,
      label: attr.type,
      startOffset: attr.start || 0,
      endOffset: attr.end || 0,
      nativeText: attr.nativeText,
      value: attr.value,
      requestedAt: timeWithOffset,
      createdAt: timeWithOffset,
      updatedAt: timeWithOffset,
      annotationId: `nazgul-attr-${attr.id}`,
      parentAnnotationId,
    };
  });
}
